import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';
import { api, RealmDefinition } from '../api';

interface Props { session: Session; }

const STATUS_OPTIONS = ['open', 'recommended', 'full', 'locked', 'archived'];
const STATUS_COLORS: Record<string, string> = {
  open: 'badge-ok',
  recommended: 'badge-info',
  full: 'badge-warn',
  locked: 'badge-muted',
  merging: 'badge-warn',
  archived: 'badge-muted',
};

export default function Realms({ session }: Props) {
  const [realms, setRealms] = useState<RealmDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newMax, setNewMax] = useState(10000);
  const [busy, setBusy] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [mergeSources, setMergeSources] = useState<string[]>([]);
  const [mergeTarget, setMergeTarget] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mergeAnalysis, setMergeAnalysis] = useState<any>(null);

  const reload = () => {
    api.listRealms(session.gameId).then(setRealms).catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId]);

  const create = async () => {
    if (!newId.trim() || !newName.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.createRealm(session.gameId, {
        realm_id: newId.trim(),
        display_name: newName.trim(),
        status: 'open',
        max_players: newMax,
        created_at: Date.now(),
        merge_history: [],
        tags: [],
        config_overrides: {},
      });
      setNewId(''); setNewName(''); setShowCreate(false);
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const changeStatus = async (realmId: string, status: string) => {
    setBusy(true); setError(null);
    try {
      await api.setRealmStatus(session.gameId, realmId, status);
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const remove = async (realmId: string) => {
    setBusy(true); setError(null);
    try {
      await api.deleteRealm(session.gameId, realmId);
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader
        title="Realms"
        subtitle="Manage servers/shards for this game."
        actions={
          <button className="btn btn-primary" onClick={() => setShowCreate(v => !v)}>
            {showCreate ? 'Cancel' : 'Create realm'}
          </button>
        }
      />

      {error && <div className="error-banner">{error}</div>}

      {showCreate && (
        <div className="card mb-5 p-4">
          <h3 className="text-sm font-semibold mb-3">New Realm</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs text-fg-muted block mb-1">Realm ID</label>
              <input className="input w-full" placeholder="s1" value={newId} onChange={e => setNewId(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-fg-muted block mb-1">Display Name</label>
              <input className="input w-full" placeholder="Server 1" value={newName} onChange={e => setNewName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-fg-muted block mb-1">Max Players</label>
              <input className="input w-full" type="number" value={newMax} onChange={e => setNewMax(+e.target.value)} />
            </div>
          </div>
          <button className="btn btn-primary" disabled={busy || !newId.trim()} onClick={create}>Create</button>
        </div>
      )}

      {realms.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Realm ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Max Players</th>
                <th>Tags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {realms.map(r => (
                <tr key={r.realm_id}>
                  <td className="font-medium font-mono text-sm">{r.realm_id}</td>
                  <td>{r.display_name}</td>
                  <td>
                    <span className={`badge ${STATUS_COLORS[r.status] || 'badge-muted'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>{r.max_players.toLocaleString()}</td>
                  <td>
                    {r.tags.length > 0 ? r.tags.map(t => (
                      <span key={t} className="badge badge-muted mr-1">{t}</span>
                    )) : <span className="text-fg-muted">-</span>}
                  </td>
                  <td className="flex gap-2">
                    <select
                      className="input text-xs"
                      value={r.status}
                      disabled={busy}
                      onChange={e => changeStatus(r.realm_id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {r.status === 'archived' && (
                      <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => remove(r.realm_id)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Merge Wizard */}
      {realms.length >= 2 && (
        <div className="mt-5">
          <button className="btn" onClick={() => { setShowMerge(v => !v); setMergeAnalysis(null); }}>
            {showMerge ? 'Cancel merge' : 'Merge realms'}
          </button>
          {showMerge && (
            <div className="card mt-3 p-4">
              <h3 className="text-sm font-semibold mb-3">Merge Wizard</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="text-xs text-fg-muted block mb-1">Source Realms (to absorb)</label>
                  <select
                    className="input w-full"
                    multiple
                    size={Math.min(realms.length, 5)}
                    value={mergeSources}
                    onChange={e => setMergeSources(Array.from(e.target.selectedOptions, o => o.value))}
                  >
                    {realms.filter(r => r.status !== 'archived' && r.realm_id !== mergeTarget).map(r => (
                      <option key={r.realm_id} value={r.realm_id}>{r.display_name} ({r.realm_id})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-fg-muted block mb-1">Target Realm (absorbs sources)</label>
                  <select
                    className="input w-full"
                    value={mergeTarget}
                    onChange={e => { setMergeTarget(e.target.value); setMergeAnalysis(null); }}
                  >
                    <option value="">Select target…</option>
                    {realms.filter(r => r.status !== 'archived' && !mergeSources.includes(r.realm_id)).map(r => (
                      <option key={r.realm_id} value={r.realm_id}>{r.display_name} ({r.realm_id})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mb-3">
                <button
                  className="btn"
                  disabled={busy || mergeSources.length === 0 || !mergeTarget}
                  onClick={async () => {
                    setBusy(true); setError(null);
                    try {
                      const a = await api.mergeAnalyze(session.gameId, mergeSources, mergeTarget);
                      setMergeAnalysis(a);
                    } catch (e) { setError((e as Error).message); }
                    finally { setBusy(false); }
                  }}
                >
                  Analyze
                </button>
                {mergeAnalysis?.can_proceed && (
                  <button
                    className="btn btn-danger"
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true); setError(null);
                      try {
                        await api.mergeExecute(session.gameId, mergeSources, mergeTarget);
                        setShowMerge(false); setMergeAnalysis(null); setMergeSources([]); setMergeTarget('');
                        reload();
                      } catch (e) { setError((e as Error).message); }
                      finally { setBusy(false); }
                    }}
                  >
                    Execute Merge
                  </button>
                )}
              </div>
              {mergeAnalysis && (
                <div className="text-sm">
                  <div className={`font-medium mb-2 ${mergeAnalysis.can_proceed ? 'text-green-500' : 'text-red-500'}`}>
                    {mergeAnalysis.can_proceed ? 'Merge can proceed' : 'Merge blocked — resolve conflicts first'}
                  </div>
                  {mergeAnalysis.conflicts.length > 0 && (
                    <ul className="list-disc pl-5 text-xs text-fg-muted space-y-1">
                      {mergeAnalysis.conflicts.map((c: { description: string }, i: number) => (
                        <li key={i}>{c.description}</li>
                      ))}
                    </ul>
                  )}
                  {mergeAnalysis.conflicts.length === 0 && (
                    <p className="text-xs text-fg-muted">No conflicts detected.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {realms.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No realms configured</div>
          <div className="text-xs text-fg-muted">
            Create a realm to partition player state by server. Single-server games use the "default" realm automatically.
          </div>
        </div>
      )}
    </Container>
  );
}
